import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Loader2, Calendar, Clock, ShieldCheck, Phone, MapPin, Building2,
  AlertTriangle, Star, CreditCard, FileText, Download,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOAST_OPTIONS = {
  style: { background: "#F9F7F2", border: "1px solid #B1D8B7", color: "#1A1A1A" },
};

function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(`${s}T00:00:00`);
    return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  } catch { return s; }
}
function fmtTime12(t) {
  if (!t) return "";
  const [hh, mm] = t.split(":");
  const h = parseInt(hh, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${mm} ${ampm}`;
}
function fmtUSD(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n) || 0);
}

export default function PublicInvoicePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null); // {url, label}
  // Stripe payment flow
  const [payInFlight, setPayInFlight] = useState(false);
  const [payPolling, setPayPolling] = useState(false);
  const [payStatus, setPayStatus] = useState(null); // "pending" | "paid" | "failed" | "expired" | "canceled"

  const fetchInvoice = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/invoice/${token}`);
      setData(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Invoice not found");
    } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  // ----- Stripe Checkout: kick-off -----
  const handlePayNow = async () => {
    if (payInFlight) return;
    setPayInFlight(true);
    try {
      const { data: resp } = await axios.post(`${API}/public/invoice/${token}/checkout`, {
        origin_url: window.location.origin,
      });
      if (!resp?.url) throw new Error("No checkout URL returned");
      // Persist the session id so the polling effect can pick up after redirect
      try { sessionStorage.setItem(`gow_pay_${token}`, resp.session_id); } catch (_) {}
      window.location.href = resp.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start payment. Please try again.");
      setPayInFlight(false);
    }
  };

  // ----- Stripe Checkout: poll after return -----
  // Stripe redirects to `?session_id=...` (success) or `?canceled=1` (cancel).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("canceled")) {
      setPayStatus("canceled");
      toast.error("Payment canceled.", TOAST_OPTIONS);
      // Clean URL so the toast doesn't fire again on refresh
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const MAX = 12;       // ~36s total — covers webhook race
    const DELAY = 3000;
    setPayPolling(true);
    setPayStatus("pending");
    const tick = async () => {
      attempts += 1;
      try {
        const { data: s } = await axios.get(`${API}/public/invoice/${token}/checkout/status/${sessionId}`);
        if (cancelled) return;
        if ((s.payment_status || "").toLowerCase() === "paid") {
          setPayStatus("paid");
          setPayPolling(false);
          toast.success("Payment received — thank you!", TOAST_OPTIONS);
          // Refresh the invoice so the UI flips to PAID watermark + cached values
          fetchInvoice();
          try { sessionStorage.removeItem(`gow_pay_${token}`); } catch (_) {}
          return;
        }
        if ((s.status || "").toLowerCase() === "expired") {
          setPayStatus("expired");
          setPayPolling(false);
          toast.error("Payment session expired. Please try again.");
          return;
        }
        if (attempts >= MAX) {
          setPayPolling(false);
          setPayStatus("pending");
          toast.message("Still processing — refresh in a minute to see updated status.");
          return;
        }
        setTimeout(tick, DELAY);
      } catch (err) {
        if (cancelled) return;
        if (attempts >= MAX) {
          setPayPolling(false);
          toast.error("Could not confirm payment status. Please contact support.");
          return;
        }
        setTimeout(tick, DELAY);
      }
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Backend-rendered PDF download (works on iOS Safari — no client-side canvas).
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const handleDownloadPdf = async () => {
    if (!data?.invoice) return;
    setGeneratingPdf(true);
    const filename = `invoice-${data.invoice.invoice_number || "georgia-outdoor-worx"}.pdf`;
    // Backend-generated PDF. This replaces `html2pdf.js`, which silently
    // fails on iOS Safari (Blob downloads are blocked) — the exact bug
    // clients on iPhone were hitting in production. A real HTTP file
    // download works on every browser and mobile device.
    try {
      const resp = await fetch(`${API}/invoice/${token}/pdf`);
      if (!resp.ok) throw new Error(`pdf ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      // iOS Safari requires the anchor to be in the DOM and to open in a
      // new tab for large-ish blobs. `target="_blank"` is a defensive
      // fallback when the browser can't trigger a direct download.
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 1000);
      toast.success("Invoice PDF downloaded");
    } catch (err) {
      console.error("[public-invoice] pdf failed:", err);
      // Last-resort fallback: navigate the tab to the PDF URL. iOS Safari
      // will open it inline where the user can tap Share → Save to Files.
      try {
        window.location.assign(`${API}/invoice/${token}/pdf`);
      } catch (_) {
        toast.error("Could not generate PDF. Please try again.");
      }
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-forest-600" />
      </div>
    );
  }

  // Error / not found
  if (error || !data) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-3" />
          <h1 className="text-xl font-bold text-stone-800 mb-2">Invoice Unavailable</h1>
          <p className="text-stone-600 text-sm">{error || "We couldn't find this invoice."}</p>
        </div>
      </div>
    );
  }

  // Expired token
  if (data.expired) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="invoice-expired">
          <Clock className="w-12 h-12 mx-auto text-amber-500 mb-3" />
          <h1 className="text-xl font-bold text-stone-800 mb-2">Link Expired</h1>
          <p className="text-stone-600 text-sm mb-4">{data.message}</p>
          <p className="text-xs text-stone-400">
            Please contact <a className="text-forest-700 underline" href="mailto:contactus@georgiaoutdoorworx.com">contactus@georgiaoutdoorworx.com</a> for a fresh link.
          </p>
        </div>
      </div>
    );
  }

  const inv = data.invoice || {};
  const client = data.client || {};
  const service = data.service;
  const photos = data.photos || [];
  const business = data.business || {};
  const reviewUrl = data.google_review_url || "";

  const photoUrl = (id) => `${API}/invoice/${token}/photo/${id}`;
  const isPaid = inv.status === "paid" || inv.paid_in_advance;

  return (
    <div className="min-h-screen bg-stone-50 text-stone-800 relative" data-testid="public-invoice-page">
      <Toaster position="top-right" toastOptions={TOAST_OPTIONS} />

      {/* PAID watermark — diagonal across the page */}
      {isPaid && (
        <div
          aria-hidden="true"
          data-testid="public-paid-stamp"
          className="pointer-events-none fixed inset-0 flex items-center justify-center z-[1]"
          style={{ mixBlendMode: "multiply" }}
        >
          <div
            className="border-[10px] border-emerald-600 text-emerald-600 font-black tracking-[0.3em] text-7xl md:text-8xl px-8 py-2 rounded-2xl opacity-20 select-none"
            style={{ transform: "rotate(-22deg)" }}
          >
            PAID
          </div>
        </div>
      )}

      {/* Header strip */}
      <div className="bg-gradient-to-r from-forest-700 to-emerald-600 text-white relative z-10">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider opacity-80">Georgia Outdoor Worx</p>
            <h1 className="text-2xl font-bold">Thank you for choosing us!</h1>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2 text-sm">
              <ShieldCheck className="w-4 h-4" />
              <span>Secure link · expires in 72h</span>
            </div>
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={generatingPdf}
              data-testid="download-pdf-top"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/15 hover:bg-white/25 text-white border border-white/30 transition-colors disabled:opacity-50"
            >
              {generatingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              {generatingPdf ? "Generating…" : "Download PDF"}
            </button>
          </div>
        </div>
      </div>

      <main className="relative z-10 max-w-4xl mx-auto px-6 py-8 space-y-6">

        {/* Invoice Summary Card */}
        <section className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
          <div className="px-6 py-5 border-b border-stone-100 flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-stone-500">Invoice</p>
              <p className="text-xl font-bold text-stone-800">#{inv.invoice_number}</p>
              <p className="text-xs text-stone-500 mt-1">Issued {inv.date} · Due {inv.due_date || "—"}</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider text-stone-500">Amount {isPaid ? "Paid" : "Due"}</p>
              <p className={`text-3xl font-bold ${isPaid ? "text-stone-400 line-through" : "text-forest-700"}`}>{fmtUSD(inv.total_amount)}</p>
              {isPaid && (
                <span className="inline-flex items-center gap-1 mt-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-300">
                  <Star className="w-3 h-3 fill-emerald-600 text-emerald-600" /> Paid in full · thank you!
                </span>
              )}
            </div>
          </div>

          {/* Items */}
          <div className="px-6 py-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-stone-100">
                  <th className="py-2">Service</th>
                  <th className="py-2 text-center">Qty</th>
                  <th className="py-2 text-right">Unit</th>
                  <th className="py-2 text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {(inv.items || []).map((it, i) => (
                  <tr key={i} className="border-b border-stone-50">
                    <td className="py-2">{it.description}</td>
                    <td className="py-2 text-center">{it.quantity}</td>
                    <td className="py-2 text-right">{fmtUSD(it.unit_price)}</td>
                    <td className="py-2 text-right font-medium">{fmtUSD((it.quantity || 0) * (it.unit_price || 0))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="3" className="pt-3 text-right font-semibold text-stone-700">Total</td>
                  <td className="pt-3 text-right font-bold text-forest-700">{fmtUSD(inv.total_amount)}</td>
                </tr>
              </tfoot>
            </table>
            {inv.notes && (
              <p className="mt-4 text-xs text-stone-500 italic"><FileText className="w-3 h-3 inline mr-1" />{inv.notes}</p>
            )}
          </div>

          {/* Pay CTA */}
          {!isPaid && (
            <div className="px-6 py-4 bg-stone-50 border-t border-stone-100">
              {payPolling ? (
                <button
                  disabled
                  data-testid="pay-polling-btn"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-stone-200 text-stone-600 font-semibold cursor-not-allowed"
                >
                  <Loader2 className="w-5 h-5 animate-spin" /> Confirming payment...
                </button>
              ) : (
                <button
                  onClick={handlePayNow}
                  disabled={payInFlight}
                  data-testid="pay-now-btn"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-forest-700 hover:bg-forest-800 text-white font-semibold transition-colors disabled:opacity-60 disabled:cursor-wait shadow-sm"
                >
                  {payInFlight ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                  {payInFlight ? "Opening secure checkout..." : `Pay ${fmtUSD(inv.total_amount)} securely`}
                </button>
              )}
              <p className="text-[11px] text-stone-400 mt-2">
                Secured by Stripe · Card · Debit · Apple Pay. You'll return here automatically after payment.
              </p>
              {payStatus === "canceled" && (
                <p className="text-xs text-amber-600 mt-1" data-testid="pay-canceled-msg">
                  Payment was canceled. You can try again any time.
                </p>
              )}
            </div>
          )}
        </section>

        {/* Client & Bank Details */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5">
            <p className="text-xs uppercase tracking-wider text-stone-500 mb-2">Bill to</p>
            <p className="font-bold text-stone-800">{client.name}</p>
            {client.address && <p className="text-sm text-stone-600 mt-1">{client.address}</p>}
            {(client.city || client.state) && (
              <p className="text-sm text-stone-600">{[client.city, client.state, client.zip_code].filter(Boolean).join(", ")}</p>
            )}
          </div>
          {inv.show_bank_details !== false && (
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5">
              <p className="text-xs uppercase tracking-wider text-stone-500 mb-2">Payment by bank transfer</p>
              <p className="text-sm"><strong>Bank:</strong> Truist Bank</p>
              <p className="text-sm"><strong>Account:</strong> 1110027939728</p>
              <p className="text-sm"><strong>Routing:</strong> 061113415</p>
              <p className="text-sm"><strong>Beneficiary:</strong> Georgia Outdoor Worx LLC · EIN 39-3769032</p>
              <p className="text-[11px] text-stone-400 mt-2">Please include invoice #{inv.invoice_number} on your transfer.</p>
            </div>
          )}
        </section>

        {/* Service performed */}
        {service && (service.scheduled_date || service.actual_start_time) && (
          <section className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5" data-testid="service-block">
            <p className="text-xs uppercase tracking-wider text-stone-500 mb-3">Service Performed</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 text-forest-600 mt-0.5" />
                <div>
                  <p className="text-stone-500 text-xs">Date</p>
                  <p className="font-medium">{fmtDate(service.scheduled_date)}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-forest-600 mt-0.5" />
                <div>
                  <p className="text-stone-500 text-xs">Time</p>
                  <p className="font-medium">
                    {fmtTime12(service.start_time)} – {fmtTime12(service.end_time)}
                  </p>
                </div>
              </div>
            </div>
            {service.service_labels?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {service.service_labels.map((s, i) => (
                  <span key={i} className="px-2 py-1 rounded-full text-xs font-medium bg-forest-50 text-forest-700 border border-forest-200">{s}</span>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Evidence photos */}
        {photos.length > 0 && (
          <section className="bg-white rounded-2xl shadow-sm border border-stone-200 p-5" data-testid="photos-block">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-wider text-stone-500">Job Evidence</p>
              <p className="text-xs text-stone-400">{photos.length} photo{photos.length === 1 ? "" : "s"}</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {photos.map((p) => (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => setLightbox({ url: photoUrl(p.id), label: p.original_filename })}
                  className="aspect-square rounded-xl overflow-hidden bg-stone-100 border border-stone-200 hover:ring-2 hover:ring-forest-400 transition-all"
                  data-testid={`public-photo-${p.id}`}
                >
                  <img
                    src={photoUrl(p.id)}
                    alt={p.original_filename || "Job evidence"}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Google review CTA */}
        {reviewUrl && (
          <section className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl p-6 text-center" data-testid="review-cta">
            <div className="flex items-center justify-center gap-1.5 mb-2" data-testid="review-stars" aria-label="5 stars">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="w-7 h-7 text-amber-500 fill-amber-400 drop-shadow-sm" />
              ))}
            </div>
            <h2 className="text-lg font-bold text-stone-800 mb-1">Help our small business grow!</h2>
            <p className="text-sm text-stone-600 mb-4">Loved our work? Your 5-star review on Google makes a huge difference.</p>
            <a
              href={reviewUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="review-link"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold transition-colors shadow-sm hover:shadow-md"
            >
              <Star className="w-4 h-4 fill-white" /> Rate us on Google
            </a>
          </section>
        )}

        {/* Big "Save a copy" CTA — for clients who want to archive the invoice */}
        <section className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 text-center" data-testid="download-cta">
          <Download className="w-7 h-7 mx-auto text-forest-600 mb-2" />
          <h2 className="text-lg font-bold text-stone-800 mb-1">Want to save a copy?</h2>
          <p className="text-sm text-stone-500 mb-4">Download a full PDF of this invoice for your records.</p>
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={generatingPdf}
            data-testid="download-pdf-bottom"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-forest-700 hover:bg-forest-800 text-white font-semibold transition-colors disabled:opacity-60"
          >
            {generatingPdf ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
            {generatingPdf ? "Preparing PDF…" : "Download Invoice PDF"}
          </button>
        </section>

        {/* Footer */}
        <footer className="text-center text-xs text-stone-400 pt-4 pb-8 space-y-1">
          <p className="flex items-center justify-center gap-2"><Building2 className="w-3 h-3" />{business.name} · Fully Licensed and Insured</p>
          {business.phone && <p className="flex items-center justify-center gap-2"><Phone className="w-3 h-3" />{business.phone}</p>}
          {business.address && <p className="flex items-center justify-center gap-2"><MapPin className="w-3 h-3" />{business.address}</p>}
        </footer>
      </main>

      {/* Lightbox overlay */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="public-invoice-lightbox"
        >
          <img src={lightbox.url} alt={lightbox.label || ""} className="max-w-full max-h-full rounded-xl shadow-2xl" />
        </div>
      )}
    </div>
  );
}
